import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  type ClientTrainingProgram,
  type ClientTrainingProgramDetails,
} from "@fitness-app/data-models/entities/TrainingProgram";

import { type AsyncThunkCreator } from "../../../index";
import { RequestStatus } from "../../../../enums/requestStatus";
import { TRAINEE_PROGRAM_REDUCER_NAME } from "../types";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1_000;

type Payload = {
  id: string;
};

export const fetchProgramWithDetails = createAsyncThunk<
  {
    plan: ClientTrainingProgram;
    details: ClientTrainingProgramDetails | null;
    additionalDetails: ClientTrainingProgramDetails[];
    fetchedAt: number;
  },
  Payload,
  AsyncThunkCreator<string>
>(
  `${TRAINEE_PROGRAM_REDUCER_NAME}/fetchProgramWithDetails`,
  async (payload, { rejectWithValue, extra: { db }, getState, requestId, signal }) => {
    for (let attempt = 0; ; attempt += 1) {
      if (signal.aborted || getState().traineeProgram.programRequestId !== requestId) {
        return rejectWithValue("Program request superseded");
      }

      const { error, data } = await db
        .from("client_training_program")
        .select("*, details:client_training_program_details (*)")
        .eq("id", payload.id)
        .single();

      if (!error) {
        const { details, ...plan } = data;
        const detailsList = (
          Array.isArray(details) ? details : details ? [details] : []
        ) as ClientTrainingProgramDetails[];

        return {
          plan: plan as ClientTrainingProgram,
          details: detailsList[0] ?? null,
          additionalDetails: detailsList.slice(1),
          fetchedAt: Date.now(),
        };
      }

      if (attempt === MAX_RETRIES) return rejectWithValue(error.message);
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * 2 ** attempt));
    }
  },
  {
    condition: ({ id }, { getState }) => {
      const program = getState().traineeProgram;
      return program.programId !== id || program.programStatus !== RequestStatus.FETCHING;
    },
  },
);
